import React, { useState, useEffect } from 'react';
import { CheckIcon, ExclamationTriangleIcon, ArrowPathIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import VerificationCodeInput from '../../common/VerificationCodeInput';
import { getFunctions, httpsCallable } from 'firebase/functions';
import '../../../styles/email-verification-screen.css';


const EmailVerificationScreen = ({ 
  email,
  verificationId,
  onVerificationSuccess, 
  onGoBack,
  onError
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [canResend, setCanResend] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(60);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Auto-focus on mount for accessibility
  useEffect(() => {
    // Focus management for inline component
    const timer = setTimeout(() => {
      const firstInput = document.querySelector('.verification-code-input input');
      if (firstInput) {
        firstInput.focus();
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  // Start countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setResendCountdown(prev => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Reset countdown when resending
  const resetCountdown = () => {
    setCanResend(false);
    setResendCountdown(60);
  };

  const handleCodeChange = (code) => {
    setVerificationCode(code);
    setError('');
    setResendSuccess(false);
  };

  const handleCodeComplete = async (code) => {
    if (code.length === 6) {
      await verifyCode(code);
    }
  };

  const verifyCode = async (code) => {
    setIsVerifying(true);
    setError('');

    try {
      const functions = getFunctions();
      const verifyEmailCode = httpsCallable(functions, 'verifyEmailCode');
      
      const result = await verifyEmailCode({
        email,
        verificationCode: code
      });

      if (result.data.success) {
        // Email verified successfully, proceed to account completion
        onVerificationSuccess({
          email: result.data.email,
          isVerified: true,
          message: result.data.message
        });
      }
    } catch (error) {
      console.error('Verification error:', error);
      
      let errorMessage = 'Verification failed. Please try again.';
      
      if (error.code === 'functions/invalid-argument') {
        if (error.message.includes('Invalid verification code')) {
          errorMessage = 'Invalid verification code. Please check and try again.';
        } else if (error.message.includes('expired')) {
          errorMessage = 'Verification code has expired. Please request a new one.';
        }
      } else if (error.code === 'functions/resource-exhausted') {
        errorMessage = 'Too many attempts. Please request a new verification code.';
      } else if (error.code === 'functions/deadline-exceeded') {
        errorMessage = 'Verification code has expired. Please request a new one.';
      } else if (error.code === 'functions/not-found') {
        errorMessage = 'Verification session not found. Please start over.';
      } else if (error.code === 'functions/already-exists') {
        errorMessage = 'An account with this email already exists. Please try logging in instead.';
      }
      
      setError(errorMessage);
      setVerificationCode('');
      
      // Notify parent of error for potential handling
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError('');
    setResendSuccess(false);

    try {
      const functions = getFunctions();
      const resendVerificationCode = httpsCallable(functions, 'resendVerificationCode');
      
      const result = await resendVerificationCode({ email });

      if (result.data.success) {
        setResendSuccess(true);
        resetCountdown();
        setVerificationCode('');
      }
    } catch (error) {
      console.error('Resend error:', error);
      
      let errorMessage = 'Failed to resend code. Please try again.';
      
      if (error.code === 'functions/resource-exhausted') {
        errorMessage = 'Too many resend requests. Please wait before requesting another code.';
      } else if (error.code === 'functions/not-found') {
        errorMessage = 'Verification session not found. Please start over.';
      }
      
      setError(errorMessage);
      
      // Notify parent of error
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsResending(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="email-verification-screen">
      {/* Back Button */}
      {onGoBack && (
        <button
          onClick={onGoBack}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100 transition-colors mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </button>
      )}
      
      {/* Header */}
      <div className="text-center mb-8">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-purple-100 to-blue-100 dark:from-purple-900/50 dark:to-blue-900/50 mb-6">
          <svg className="h-8 w-8 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Check your email</h2>
        <p className="text-gray-600 dark:text-gray-300">
          We sent a verification code to
        </p>
        <p className="font-semibold text-gray-900 dark:text-white mt-1">{email}</p>
        
        {/* Spam Folder Notice */}
        <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
            📬 Don't see it? Check your spam folder
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
            If you find it there, mark it as "Not Spam" to ensure future emails arrive in your inbox
          </p>
        </div>
      </div>

          {/* Verification Code Input */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-4 text-center">
              Enter verification code
            </label>
            <VerificationCodeInput
              value={verificationCode}
              onChange={handleCodeChange}
              onComplete={handleCodeComplete}
              disabled={isVerifying}
              error={!!error}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-800 dark:text-red-200" id="verification-error">
                  {error}
                </p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {resendSuccess && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3">
              <CheckIcon className="h-5 w-5 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-green-800 dark:text-green-200 font-medium">
                  New verification code sent to your email
                </p>
                <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                  Remember to check your spam folder if you don't see it
                </p>
              </div>
            </div>
          )}

          {/* Verify Button */}
          <button
            onClick={() => verifyCode(verificationCode)}
            disabled={verificationCode.length !== 6 || isVerifying}
            className={`
              w-full py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200
              ${verificationCode.length === 6 && !isVerifying
                ? 'bg-purple-600 hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2'
                : 'bg-gray-200 dark:bg-gray-700 cursor-not-allowed opacity-60'
              }
            `}
          >
            {isVerifying ? (
              <div className="flex items-center justify-center gap-2">
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                Verifying...
              </div>
            ) : (
              'Verify Email'
            )}
          </button>

          {/* Resend Section */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              Didn't receive the code?
            </p>
            
            {canResend ? (
              <button
                onClick={handleResendCode}
                disabled={isResending}
                className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-semibold text-sm transition-colors disabled:opacity-50"
              >
                {isResending ? (
                  <span className="flex items-center justify-center gap-2">
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    Sending...
                  </span>
                ) : (
                  'Send new code'
                )}
              </button>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Resend available in {formatTime(resendCountdown)}
              </p>
            )}
          </div>

      {/* Help Text */}
      <div className="mt-6 text-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          The verification code expires in 10 minutes
        </p>
      </div>
    </div>
  );
};

export default EmailVerificationScreen;